import { nameOf, UNRECOGNISED_WORD } from "@/lib/format";
import React from "react";
import api from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, PageHeader, EmptyState } from "@/components/common";
import { ChatCircleText, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const CAT_META = {
  bug: { l: "Bug", c: "#F01919" }, idea: { l: "Idea", c: "#077A39" },
  usability: { l: "Usability", c: "#034AA2" }, other: { l: "Other", c: "#6b7280" },
};
const STATUSES = ["new", "reviewed", "done"];

export default function Feedback() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["feedback"], queryFn: async () => (await api.get("/feedback")).data });
  const setStatus = async (id, status) => {
    await api.patch(`/feedback/${id}`, { status });
    toast.success("Updated");
    qc.invalidateQueries({ queryKey: ["feedback"] });
  };
  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <div>
      <PageHeader title="Feedback" subtitle={`${items.length} submission${items.length === 1 ? "" : "s"}${newCount ? ` · ${newCount} new` : ""}`} />
      {items.length === 0 ? (
        <EmptyState icon={ChatCircleText} title="No feedback yet" subtitle="Users can submit ideas and bug reports anytime via the floating Feedback button." />
      ) : (
        <div className="space-y-3">
          {items.map((i) => (
            <Card key={i.id} data-testid={`feedback-item-${i.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: CAT_META[i.category]?.c || "#6b7280" }}>{nameOf(CAT_META, i.category, {}).l || UNRECOGNISED_WORD}</span>
                    {i.rating ? <span className="flex items-center gap-0.5 text-xs font-semibold text-[#F5A623]"><Star size={13} weight="fill" />{i.rating}</span> : null}
                    <span className="text-[11px] text-[#6b7280]">{i.user_name} · <span className="capitalize">{i.role}</span> · {new Date(i.created_at).toLocaleDateString()}</span>
                    {i.page ? <span className="rounded bg-[#E9E9EB] px-1.5 py-0.5 text-[10px] text-[#475569]">{i.page}</span> : null}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-[#1f2937]">{i.message}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(i.id, s)} data-testid={`feedback-status-${i.id}-${s}`}
                      className={`rounded-md px-2 py-1 text-[11px] font-semibold capitalize transition-colors ${i.status === s ? "bg-[#034AA2] text-white" : "bg-[#E9E9EB] text-[#475569] hover:bg-[#dcdce0]"}`}>{s}</button>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
